import React, { useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useT, useLang } from '../../i18n/LanguageContext';
import strings from './DownloadPage.i18n';
import Particles from '../shared/Particles';
import FadeIn from '../shared/FadeIn'; // used in hero section only
import Footer from '../layout/Footer';
import { useApkRelease } from '../../hooks/useApkRelease';
import './DownloadPage.css';

const AppleIcon = () => (
  <svg width="22" height="26" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.37 2.73M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const AndroidIcon = () => (
  <svg width="20" height="20" viewBox="0 0 576 512" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
    <path d="M420.55 301.93a24 24 0 1 1 24-24 24 24 0 0 1-24 24m-265.1 0a24 24 0 1 1 24-24 24 24 0 0 1-24 24m273.7-144.48 47.94-83a10 10 0 1 0-17.27-10h0l-48.54 84.07a301.25 301.25 0 0 0-246.56 0L116.18 64.45a10 10 0 1 0-17.27 10h0l47.94 83C64.53 202.22 8.24 285.55 0 384h576c-8.24-98.45-64.54-181.78-146.85-226.55"/>
  </svg>
);

const PlayIcon = () => (
  <svg width="20" height="22" viewBox="0 0 20 22" xmlns="http://www.w3.org/2000/svg">
    <path d="M.48.47C.18.79 0 1.28 0 1.92v18.16c0 .64.18 1.13.49 1.45l.08.07 10.18-10.18v-.24L.56.4z" fill="#4285F4"/>
    <path d="M14.1 15.1l-3.35-3.35v-.24l3.35-3.35.08.04 3.97 2.26c1.13.64 1.13 1.69 0 2.34l-3.97 2.26z" fill="#FBBC04"/>
    <path d="M14.18 15.06L10.75 11.6 .48 21.53c.37.4.99.44 1.68.05l12.02-6.52" fill="#34A853"/>
    <path d="M14.18 8.14L2.16 1.62C1.47 1.22.85 1.27.48 1.67l10.27 9.93z" fill="#EA4335"/>
  </svg>
);

const SCREENSHOT_COUNT = 10;

const screenshotSrc = (lang, n) =>
  `/images/store-listing/${lang === 'zh' ? 'zh' : 'en'}/screenshot-${n}.png`;

const preloadImage = (src) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
};

export default function DownloadPage({ onNavigate }) {
  const t = useT(strings);
  const { lang } = useLang();
  const containerRef = useRef(null);
  const { apkUrl } = useApkRelease();

  useEffect(() => {
    ['en', 'zh'].forEach(l => {
      for (let i = 1; i <= SCREENSHOT_COUNT; i++) preloadImage(screenshotSrc(l, i));
    });
    preloadImage('/images/salesperson/and-dashboard-zh.jpg');
  }, []);

  useEffect(() => {
    const heroEl = containerRef.current?.querySelector('.dl-hero');
    const handleNavTheme = () => {
      if (!heroEl) return;
      const heroBottom = heroEl.offsetTop + heroEl.offsetHeight;
      const atHero = window.scrollY < heroBottom - 64;
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        navbar.setAttribute('data-nav-theme', atHero ? 'inverted' : 'default');
      }
    };
    window.addEventListener('scroll', handleNavTheme, { passive: true });
    handleNavTheme();
    return () => window.removeEventListener('scroll', handleNavTheme);
  }, []);


  return (
    <div className="download-page" ref={containerRef}>
      {/* Hero Section */}
      <section className="dl-hero" data-nav-theme="dark">
        <div className="dl-hero__particles" aria-hidden="true">
          <Particles
            particleColors={["#ee3a47", "#f5c03f", "#e85a55"]}
            particleCount={80}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={false}
            alphaParticles={false}
            disableRotation
          />
        </div>

        <div className="dl-hero__inner">
          <div className="dl-hero__left">
            <FadeIn>
              <div className="dl-hero__label-pill">
                <span>{t.heroLabel}</span>
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h1 className="dl-hero__title">{t.heroTitle}</h1>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="dl-hero__subtitle">{t.heroSubtitle}</p>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="dl-hero__btns-row">
                <div className="dl-hero__btns">
                  <a href="https://apps.apple.com/ng/app/sapot-ai/id6760330603" target="_blank" rel="noopener noreferrer" className="dl-btn dl-btn--light">
                    <AppleIcon />
                    <span className="dl-btn__text">
                      <span className="dl-btn__label">{t.iosLabel}</span>
                      <span className="dl-btn__store">{t.iosStore}</span>
                    </span>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.kengloon.sapotaimobile&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="dl-btn dl-btn--dark dl-btn--border">
                    <PlayIcon />
                    <span className="dl-btn__text">
                      <span className="dl-btn__label">{t.androidLabel}</span>
                      <span className="dl-btn__store">{t.androidStore}</span>
                    </span>
                  </a>
                  <a href={apkUrl || '#'} download="sapot-android.apk" className="dl-btn dl-btn--dark dl-btn--border">
                    <AndroidIcon />
                    <span className="dl-btn__text">
                      <span className="dl-btn__label">{t.apkLabel}</span>
                      <span className="dl-btn__store">{t.apkStore}</span>
                    </span>
                  </a>
                </div>
                <div className="dl-hero__qr-card">
                  <div className="dl-hero__qr-code">
                    <QRCodeSVG
                      value="https://sapot.ai/download"
                      size={125}
                      bgColor="transparent"
                      fgColor="#1A1D2E"
                      imageSettings={{
                        src: '/favicon.png',
                        height: 12,
                        width: 12,
                        excavate: true,
                      }}
                    />
                  </div>
                  <p className="dl-hero__qr-label">{t.qrTitle}</p>
                </div>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={400}>
            <div className="dl-hero__right">
              <img
                key={lang}
                src={lang === 'zh' ? '/images/salesperson/and-dashboard-zh.jpg' : '/images/salesperson/salesperson-mobile-dashboard.png'}
                alt="Sapot AI mobile app dashboard"
                className="dl-phone-img dl-phone-img--lang-switch"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Features + QR/CTA + Footer — one scrollable snap section */}
      <div className="dl-scroll-section">

      {/* Screenshots Section */}
      <section className="dl-screenshots">
        <div className="dl-screenshots__header dl-animate">
          <h2 className="dl-features__title">{t.featuresTitle}</h2>
          <p className="dl-features__subtitle">{t.featuresSubtitle}</p>
        </div>
        <div key={lang} className="dl-screenshots__track dl-screenshots__track--enter">
          {Array.from({ length: SCREENSHOT_COUNT }, (_, i) => (
            <img
              key={i}
              src={screenshotSrc(lang, i + 1)}
              alt={`Sapot AI screenshot ${i + 1}`}
              className="dl-screenshot-img"
              style={{ animationDelay: `${i * 40}ms` }}
            />
          ))}
        </div>
      </section>

      {/* QR + CTA Section */}
      <section className="dl-qrcta">
        <div className="dl-qrcta__inner">
          {/* QR block */}
          <div className="dl-animate">
            <div className="dl-qr__inner">
              <div className="dl-qr__code-box">
                <QRCodeSVG
                  value="https://sapot.ai/download"
                  size={120}
                  bgColor="transparent"
                  fgColor="#1a1a2e"
                  imageSettings={{
                    src: '/favicon.png',
                    height: 24,
                    width: 24,
                    excavate: true,
                  }}
                />
              </div>
              <div className="dl-qr__text">
                <h3 className="dl-qr__title">{t.qrTitle}</h3>
                <p className="dl-qr__subtitle">{t.qrSubtitle}</p>
              </div>
              <div className="dl-qr__divider" />
              <div className="dl-qr__reqs">
                <div className="dl-qr__req-row"><AppleIcon /><span>{t.req1}</span></div>
                <div className="dl-qr__req-row"><AndroidIcon /><span>{t.req2}</span></div>
              </div>
            </div>
          </div>

          {/* CTA block */}
          <div className="dl-animate" style={{ animationDelay: '100ms' }}>
            <div className="dl-cta__inner">
              <h2 className="dl-cta__title">{t.ctaTitle}</h2>
              <p className="dl-cta__subtitle">{t.ctaSubtitle}</p>
              <div className="dl-cta__btns">
                <a href="https://apps.apple.com/ng/app/sapot-ai/id6760330603" target="_blank" rel="noopener noreferrer" className="dl-btn dl-btn--light">
                  <AppleIcon />
                  <span className="dl-btn__text">
                    <span className="dl-btn__label">{t.iosLabel}</span>
                    <span className="dl-btn__store">{t.iosStore}</span>
                  </span>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.kengloon.sapotaimobile&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="dl-btn dl-btn--dark-outline">
                  <PlayIcon />
                  <span className="dl-btn__text">
                    <span className="dl-btn__label">{t.androidLabel}</span>
                    <span className="dl-btn__store">{t.androidStore}</span>
                  </span>
                </a>
                <a href={apkUrl || '#'} download="sapot-android.apk" className="dl-btn dl-btn--dark-outline">
                  <AndroidIcon />
                  <span className="dl-btn__text">
                    <span className="dl-btn__label">{t.apkLabel}</span>
                    <span className="dl-btn__store">{t.apkStore}</span>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="dl-footer-snap">
        <Footer onNavigate={onNavigate} />
      </div>

      </div>{/* end dl-scroll-section */}
    </div>
  );
}
